import React, { useState } from 'react'
import { ChevronDown, Loader2 } from 'lucide-react'
// Se importa de cada archivo y no del barrel de ui/: ese arrastra
// OrganizationContext e ImageUploader, que cargan el cliente de Supabase real y
// hacen imposible probar este componente en aislamiento.
import { Button } from '../../../ui/button'
import { Card, CardContent } from '../../../ui/card'
import { Checkbox } from '../../../ui/checkbox'
import { Badge } from '../../../ui/badge'
import { agruparPorPrendaYTalla } from '../../../../lib/uniformesAgrupar'

// Lo que hay por pedir, AGRUPADO COMO LO PIDE EL PROVEEDOR: por prenda, y dentro
// por talla con su cantidad.
//
// Antes era una lista plana de un renglon por jugador. Eso era una invencion de la
// pantalla: al proveedor no se le manda "Daniel necesita un jersey", se le manda
// "Jersey Flag: 3 medianas, 2 chicas". El dueno lo vio preguntando "¿como agrupo
// por prenda?".
//
// Las que NO tienen talla van al final de su prenda, una por jugador: ahi es donde
// hay trabajo y donde se captura.

const nombresDe = (items, jugadorById, tope = 3) => {
  const nombres = items
    .map((i) => jugadorById[i.jugador_id]?.nombre_completo)
    .filter(Boolean)
  if (nombres.length <= tope) return nombres.join(', ')
  return `${nombres.slice(0, tope).join(', ')} y ${nombres.length - tope} mas`
}

const ListaNecesidad = ({
  entregas = [], jugadorById = {}, prendaById = {},
  seleccion, onToggle, onCapturarTalla, guardando,
  temporadaPrendaPorId, onAgregarATemporada,
  vacioTexto = 'Ninguna prenda en este grupo.',
}) => {
  const [cerrados, setCerrados] = useState(new Set())

  if (entregas.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {vacioTexto}
        </CardContent>
      </Card>
    )
  }

  const grupos = agruparPorPrendaYTalla(entregas, prendaById)

  const toggleGrupo = (prendaId) => setCerrados((prev) => {
    const next = new Set(prev)
    if (next.has(prendaId)) next.delete(prendaId); else next.add(prendaId)
    return next
  })

  // Una linea de talla entra al pedido completa: se marca la linea, no jugador por
  // jugador. Es la unidad que el proveedor produce.
  const idsDeLinea = (items) => items.filter((i) => i.asignacionId).map((i) => i.id)
  const lineaMarcada = (items) => {
    const ids = idsDeLinea(items)
    return ids.length > 0 && ids.every((id) => seleccion?.has(id))
  }

  return (
    <div className="space-y-3">
      {grupos.map((g) => {
        const abierto = !cerrados.has(g.prenda.id)
        // Una prenda puede estar aqui sin ir en la temporada: si ya tenia tallas
        // capturadas o piezas pedidas, sigue apareciendo para poder entregarla. El
        // dueno lo vio y dijo "esto no deberia pasar" -- asi que se marca y se
        // ofrece arreglarlo desde aqui mismo.
        const enTemporada = temporadaPrendaPorId ? temporadaPrendaPorId[g.prenda.id] : 'abierta'
        const fueraDeTemporada = temporadaPrendaPorId && !enTemporada
        return (
          <Card key={g.prenda.id}>
            <button
              type="button"
              onClick={() => toggleGrupo(g.prenda.id)}
              aria-expanded={abierto}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="flex min-w-0 items-center gap-2">
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${abierto ? '' : '-rotate-90'}`} />
                <span className="truncate text-sm font-semibold">{g.prenda.nombre}</span>
                {g.prenda.personalizada && (
                  <Badge variant="outline" className="shrink-0 text-[10px]">Lleva nombre</Badge>
                )}
                {fueraDeTemporada && (
                  <Badge variant="warning" className="shrink-0 text-[10px]">Fuera de la temporada</Badge>
                )}
                {enTemporada === 'cerrada' && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">Cerrada</Badge>
                )}
              </span>
              <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
                {g.total} {g.total === 1 ? 'pieza' : 'piezas'}
              </span>
            </button>

            {fueraDeTemporada && onAgregarATemporada && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-amber-50 px-4 py-2">
                <p className="text-xs text-amber-800">
                  Aparece porque ya tiene piezas capturadas o pedidas, pero no la
                  agregaste a esta temporada.
                </p>
                <Button size="sm" variant="outline" onClick={() => onAgregarATemporada(g.prenda)}>
                  Agregar a la temporada
                </Button>
              </div>
            )}

            {abierto && (
              <div className="border-t">
                {/* Lineas de talla: lo que se pide */}
                {g.tallas.map((t) => (
                  <div key={t.talla} className="flex items-center gap-3 px-4 py-2.5">
                    {onToggle && (
                      <Checkbox
                        checked={lineaMarcada(t.items)}
                        onCheckedChange={() => onToggle(idsDeLinea(t.items), !lineaMarcada(t.items))}
                        aria-label={`Seleccionar ${g.prenda.nombre} talla ${t.talla}`}
                        disabled={idsDeLinea(t.items).length === 0}
                      />
                    )}
                    <span className="w-10 shrink-0 text-sm font-medium">{t.talla}</span>
                    <span className="w-12 shrink-0 text-sm tabular-nums text-muted-foreground">
                      &times; {t.cantidad}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {nombresDe(t.items, jugadorById)}
                    </span>
                  </div>
                ))}

                {/* Sin talla: aqui esta el trabajo */}
                {g.sinTalla.length > 0 && (
                  <div className="border-t bg-muted/30">
                    <p className="px-4 pt-2.5 text-xs font-medium text-amber-700">
                      {g.sinTalla.length} sin talla — captúrala para poder pedirla
                    </p>
                    {g.sinTalla.map((e) => {
                      const guardandoEsta = guardando?.has(e.id) || false
                      return (
                        <div
                          key={e.id}
                          className={`flex items-center gap-3 px-4 py-2 transition-opacity ${guardandoEsta ? 'opacity-60' : ''}`}
                        >
                          <span className="min-w-0 flex-1 truncate text-sm">
                            {jugadorById[e.jugador_id]?.nombre_completo || 'Jugador'}
                            {e.ligadoAPago != null && (
                              <span className="ml-1.5 text-xs text-muted-foreground">
                                · se libera en el Pago {e.ligadoAPago}
                              </span>
                            )}
                          </span>
                          {onCapturarTalla && (
                            guardandoEsta ? (
                              <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Guardando...
                              </span>
                            ) : (
                              <span className="flex shrink-0 flex-wrap justify-end gap-1">
                                {(g.prenda.tallas || []).map((t) => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => onCapturarTalla(e, t)}
                                    className="rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                  >
                                    {t}
                                  </button>
                                ))}
                                {(g.prenda.tallas || []).length === 0 && (
                                  <span className="text-xs text-muted-foreground">Sin tallas configuradas</span>
                                )}
                              </span>
                            )
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}

export default ListaNecesidad
